import {
  ACHIEVEMENT_CATALOG,
  FACT_CATALOG,
  SKILL_IDS,
  SKILLS,
  STRATEGIES,
  STRATEGY_BY_ID,
  buildFactHelp,
  instantiateStrategyLesson,
  strategyWorkedExample,
  type Fact,
  type LessonActivityResponse,
  type SessionMode,
} from "@oefenheld/reken-core";
import {
  IndexedDbLearningRepository,
  type LearningRepository,
  type SessionRecord,
} from "@oefenheld/profile";
import {
  RekenheldRuntime,
  defaultLearnerProfile,
  type PresentedLearningItem,
} from "@oefenheld/reken-runtime";

const PROFILE_ID = "anthony";
const app = document.querySelector<HTMLDivElement>("#app")!;
let repository: LearningRepository;
let runtime: RekenheldRuntime;
let activeSessionId: string | null = null;
let currentItem: PresentedLearningItem | null = null;

type AchievementCard = (typeof ACHIEVEMENT_CATALOG.cards)[number];

const CARD_ARTWORK_BY_ID: Readonly<Record<string, string>> = {
  "REKEN.017.BASE": "./assets/cards/017-bruggenbouwer/web/base.webp",
  "REKEN.017.FOIL": "./assets/cards/017-bruggenbouwer/web/foil.webp",
  "REKEN.017.HOLO": "./assets/cards/017-bruggenbouwer/web/holo.webp",
  "REKEN.017.GOLD": "./assets/cards/017-bruggenbouwer/web/gold.webp",
};

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;",
  })[character]!);
}

function achievementCardMarkup(card: AchievementCard, unlocked: boolean): string {
  const artwork = CARD_ARTWORK_BY_ID[card.id];
  const title = unlocked || !card.secret ? card.title : "???";
  const copy = unlocked
    ? card.earnedCopy
    : card.secret
    ? "Geheime kaart"
    : card.lockedHint ?? "Nog te ontdekken";
  return `<article class="card ${unlocked ? card.tier.toLowerCase() : "locked"}">
    ${unlocked && artwork ? `<img class="card-art" src="${artwork}" alt="" loading="lazy" decoding="async">` : ""}
    <span class="tier">#${card.collectionNumber} · ${card.tier}</span>
    ${unlocked ? "" : `<div class="symbol">${card.secret ? "?" : "◌"}</div>`}
    <div class="card-copy"><strong>${escapeHtml(title)}</strong><p>${escapeHtml(copy)}</p></div>
  </article>`;
}

function nav(active: "home" | "cards" | "growth"): string {
  return `<nav class="nav" aria-label="Hoofdnavigatie">
    <button data-nav="home" class="${active === "home" ? "active" : ""}">⌂<br>Home</button>
    <button data-nav="cards" class="${active === "cards" ? "active" : ""}">▦<br>Mijn kaarten</button>
    <button data-nav="growth" class="${active === "growth" ? "active" : ""}">↗<br>Mijn groei</button>
  </nav>`;
}

function shell(
  content: string,
  active: "home" | "cards" | "growth" = "home",
  focus = false,
): void {
  app.innerHTML = `<main class="shell${focus ? " focus" : ""}">${content}</main>${focus ? "" : nav(active)}`;
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  if (!focus) {
    document.querySelectorAll<HTMLButtonElement>("[data-nav]").forEach((button) => {
      button.addEventListener("click", () => {
        const target = button.dataset.nav;
        if (target === "home") void renderHome();
        if (target === "cards") void renderCards();
        if (target === "growth") void renderGrowth();
      });
    });
  }
}

function weekDates(): string[] {
  const now = new Date();
  const weekday = Number(new Intl.DateTimeFormat("en", {
    timeZone: "Europe/Amsterdam", weekday: "short",
  }).formatToParts(now).find((part) => part.type === "weekday")?.value
    ?.replace(/Mon/, "1").replace(/Tue/, "2").replace(/Wed/, "3")
    .replace(/Thu/, "4").replace(/Fri/, "5").replace(/Sat/, "6").replace(/Sun/, "7")) || 1;
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(now);
    date.setDate(now.getDate() - weekday + 1 + index);
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Amsterdam", year: "numeric", month: "2-digit", day: "2-digit",
    }).format(date);
  });
}

async function latestResumableSession(): Promise<SessionRecord | null> {
  return runtime.resumableSession(PROFILE_ID);
}

async function renderHome(): Promise<void> {
  const [profile, sessions, unlocks] = await Promise.all([
    repository.getProfile(PROFILE_ID),
    repository.listSessionsForProfile(PROFILE_ID),
    repository.listAchievementUnlocks(PROFILE_ID),
  ]);
  const resumable = await latestResumableSession();
  const dates = weekDates();
  const practiced = new Set(sessions.filter((session) => !session.earlyExit).map((session) => session.localDate));
  const practicedDays = dates.filter((date) => practiced.has(date)).length;
  const earned = new Set(unlocks.map((unlock) => unlock.cardId));
  const nearCard = ACHIEVEMENT_CATALOG.cards.find((card) => !earned.has(card.id) && !card.secret);
  shell(`
    <div class="row between"><span class="brand">Rekenheld</span><button id="parent" class="quiet">Voor ouders</button></div>
    <section class="hero">
      <p class="eyebrow">Hoi ${escapeHtml(profile?.displayName ?? "held")}</p>
      <h1>${resumable ? "Je missie wacht op je" : "Klaar voor vandaag?"}</h1>
      <p>${resumable ? "Ga rustig verder waar je gebleven was." : "Een korte missie maakt je weer een beetje sterker."}</p>
      <button id="start" class="primary wide">${resumable ? "Ga verder" : "Start mijn missie"}</button>
      <p style="margin:12px 0 0;text-align:center;font-size:.88rem">Ongeveer 5 minuten</p>
    </section>
    <div class="stack">
      <section class="panel">
        <div class="row between"><strong>Deze week</strong><span class="muted">${practicedDays} heldendagen</span></div>
        <div class="week" style="margin-top:16px">
          ${["M","D","W","D","V","Z","Z"].map((day, index) => `<div class="day"><span>${day}</span><i class="dot ${practiced.has(dates[index]) ? "done" : ""}"></i></div>`).join("")}
        </div>
      </section>
      ${nearCard ? `<section class="panel"><p class="eyebrow">Bijna verdiend</p><h2>${escapeHtml(nearCard.title)} ${nearCard.tier}</h2><p class="muted">${escapeHtml(nearCard.lockedHint ?? "Blijf rustig oefenen.")}</p><button id="cards" class="secondary">Mijn kaarten</button></section>` : ""}
    </div>`);
  document.querySelector("#start")?.addEventListener("click", () => {
    if (resumable) {
      activeSessionId = resumable.id;
      if (resumable.phase === "CORE_COMPLETE" || resumable.phase === "REVEAL_PENDING") void renderSessionEnd(resumable);
      else void showNextItem();
    } else renderModeChoice();
  });
  document.querySelector("#cards")?.addEventListener("click", () => void renderCards());
  document.querySelector("#parent")?.addEventListener("click", () => void renderParent());
}

function renderModeChoice(): void {
  shell(`<section><span class="brand">Rekenheld</span><p class="eyebrow" style="margin-top:44px">Jij kiest de vorm</p><h1>Waar heb je zin in?</h1>
    <div class="mode-grid">
      <button class="mode" data-mode="SHORT"><strong>⚡ Kort</strong><span>ongeveer 3 minuten</span></button>
      <button class="mode" data-mode="NORMAL"><strong>🎯 Normaal</strong><span>ongeveer 5 minuten</span></button>
      <button class="mode" data-mode="CHALLENGE"><strong>🔥 Uitdaging</strong><span>iets meer uitdaging</span></button>
    </div>
    <button id="back" class="quiet wide" style="margin-top:16px">Terug</button></section>`, "home", true);
  document.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((button) => {
    button.addEventListener("click", () => void beginSession(button.dataset.mode as SessionMode));
  });
  document.querySelector("#back")?.addEventListener("click", () => void renderHome());
}

async function beginSession(mode: SessionMode): Promise<void> {
  const session = await runtime.startSession({ profileId: PROFILE_ID, mode });
  activeSessionId = session.id;
  await showNextItem();
}

async function showNextItem(): Promise<void> {
  if (!activeSessionId) return renderHome();
  const next = await runtime.nextItem(activeSessionId);
  if (next.kind !== "ITEM") {
    activeSessionId = next.session.id;
    currentItem = null;
    await renderSessionEnd(next.session);
    return;
  }
  currentItem = next.item;
  activeSessionId = currentItem.presentation.sessionId;
  if (currentItem.fact) renderProblem(currentItem);
  else renderStrategyLesson(currentItem);
}

function keypadHtml(): string {
  return `<div class="keypad" aria-label="Rekentoetsenbord">
    ${[1,2,3,4,5,6,7,8,9].map((number) => `<button class="key" data-digit="${number}">${number}</button>`).join("")}
    <button class="key" data-backspace aria-label="Wis laatste cijfer">←</button>
    <button class="key" data-digit="0">0</button>
    <button class="key confirm" data-confirm aria-label="Controleer antwoord">✓</button>
  </div>`;
}

function renderProblem(item: PresentedLearningItem): void {
  const fact = item.fact!;
  let answer = "";
  let firstInputAt: string | null = null;
  let editCount = 0;
  let submitting = false;
  const operator = fact.operation === "ADD" ? "+" : "−";
  shell(`<div class="topbar"><button id="stop" class="icon-button" aria-label="Stop missie">×</button><span class="brand">Rekenheld</span></div>
    <section class="problem">
      <p class="eyebrow">Jouw som</p>
      <div class="expression">${fact.left} ${operator} ${fact.right}</div>
      <div id="answer" class="answer-display empty" aria-label="Jouw antwoord"></div>
      ${keypadHtml()}
      <button id="help" class="help">Ik weet 'm even niet</button>
      <div id="feedback" class="feedback" role="status"></div>
    </section>`, "home", true);
  const display = document.querySelector<HTMLDivElement>("#answer")!;
  const feedback = document.querySelector<HTMLDivElement>("#feedback")!;
  const update = () => {
    display.textContent = answer;
    display.classList.toggle("empty", answer.length === 0);
  };
  document.querySelectorAll<HTMLButtonElement>("[data-digit]").forEach((button) => {
    button.addEventListener("click", () => {
      if (submitting || answer.length >= 3) return;
      firstInputAt ??= new Date().toISOString();
      answer += button.dataset.digit;
      update();
    });
  });
  document.querySelector<HTMLButtonElement>("[data-backspace]")?.addEventListener("click", () => {
    if (submitting || answer.length === 0) return;
    answer = answer.slice(0, -1);
    editCount += 1;
    update();
  });
  document.querySelector<HTMLButtonElement>("[data-confirm]")?.addEventListener("click", async () => {
    if (submitting || answer.length === 0) return;
    submitting = true;
    const result = await runtime.submitAnswer({
      presentationId: item.presentation.id,
      enteredAnswer: Number(answer),
      firstInputAt,
      editCount,
      helpRequested: false,
      supportUsed: false,
    });
    if (result.attempt.correct) {
      feedback.className = "feedback good";
      feedback.textContent = "Goed!";
      window.setTimeout(() => void afterLearningItem(result.session), 420);
    } else {
      feedback.className = "feedback try";
      feedback.textContent = "Nog niet. Kijk even mee.";
      window.setTimeout(() => void renderHelpAfterAttempt(fact, result.session, item.presentation.id), 500);
    }
  });
  document.querySelector("#help")?.addEventListener("click", async () => {
    if (submitting) return;
    submitting = true;
    const result = await runtime.submitAnswer({
      presentationId: item.presentation.id,
      enteredAnswer: null,
      firstInputAt,
      editCount,
      helpRequested: true,
      supportUsed: false,
    });
    feedback.className = "feedback good";
    feedback.textContent = "Geeft niks. We pakken 'm samen.";
    window.setTimeout(() => void renderHelpAfterAttempt(fact, result.session, item.presentation.id), 500);
  });
  document.querySelector("#stop")?.addEventListener("click", async () => {
    if (submitting) return;
    submitting = true;
    const stopped = await runtime.stopSession(item.presentation.sessionId);
    activeSessionId = null;
    await renderStopped(stopped);
  });
}

async function renderHelpAfterAttempt(fact: Fact, session: SessionRecord, presentationId: string): Promise<void> {
  await runtime.recordAnswerExposure(presentationId);
  const help = buildFactHelp(fact, STRATEGIES);
  shell(`<section class="lesson panel">
    <p class="eyebrow">Samen verder</p>
    <h1>${escapeHtml(help.title)}</h1>
    <div class="lesson-expression">${escapeHtml(help.expression)}</div>
    <div class="lesson-copy">${help.steps.map((line) => `<p>${escapeHtml(line)}</p>`).join("")}</div>
    <button id="continue" class="primary wide">Verder met mijn missie</button>
  </section>`, "home", true);
  document.querySelector("#continue")?.addEventListener("click", () => void afterLearningItem(session));
}

async function afterLearningItem(session: SessionRecord): Promise<void> {
  if (session.phase === "CORE_COMPLETE" || session.phase === "REVEAL_PENDING") {
    await renderSessionEnd(session);
  } else {
    await showNextItem();
  }
}

async function renderStopped(session: SessionRecord): Promise<void> {
  shell(`<section class="panel" style="text-align:center">
    <p class="eyebrow">Helemaal goed</p><h1>Tot de volgende keer</h1>
    <p class="muted">Wat je vandaag leerde, blijft gewoon bewaard.</p>
    <button id="home" class="primary wide">Naar home</button>
  </section>`, "home", true);
  document.querySelector("#home")?.addEventListener("click", () => void renderHome());
}

function renderStrategyLesson(item: PresentedLearningItem): void {
  const strategyId = item.decision.strategyId;
  if (!strategyId) {
    shell(`<section class="error-box"><h2>Deze bouwsteen komt hierna</h2><p>Deze concrete conceptactiviteit wordt nog toegevoegd.</p><button id="stop" class="primary">Terug naar home</button></section>`, "home", true);
    document.querySelector("#stop")?.addEventListener("click", async () => {
      await runtime.stopSession(item.presentation.sessionId);
      activeSessionId = null;
      await renderHome();
    });
    return;
  }
  const variant = item.decision.reason === "STRATEGY_REFRESH" ? "REFRESH" : "FULL";
  const lesson = item.presentation.lesson ?? instantiateStrategyLesson(strategyId, FACT_CATALOG, STRATEGIES, item.decision.seed, variant);
  let activityIndex = item.presentation.lessonProgress?.activityIndex ?? 0;
  let questionIndex = item.presentation.lessonProgress?.questionIndex ?? 0;
  let answer = "";
  let cancelled = false;
  let submitting = false;
  const answers = new Map<string, Record<string, number>>(Object.entries(item.presentation.lessonProgress?.answers ?? {}));
  const responses = new Map<string, LessonActivityResponse>((item.presentation.lessonProgress?.responses ?? []).map((response) => [response.activityId, response]));
  const checkpoint = () => runtime.checkpointLesson(item.presentation.id, {
    activityIndex, questionIndex, answers: Object.fromEntries(answers), responses: [...responses.values()],
  });

  const finish = async () => {
    if (cancelled) return;
    const result = await runtime.completeStrategyActivity({
      presentationId: item.presentation.id,
      response: {
        version: "lesson-response-v1", lessonId: lesson.id,
        activities: lesson.activities.map((activity) => responses.get(activity.id)!),
      },
    });
    await afterLearningItem(result.session);
  };

  const nextActivity = async () => {
    if (cancelled) return;
    const activity = lesson.activities[activityIndex];
    if (!responses.has(activity.id)) {
      responses.set(activity.id, {
        activityId: activity.id, answers: answers.get(activity.id) ?? {},
        helpRequested: false, supportUsed: false,
      });
    }
    activityIndex += 1;
    questionIndex = 0;
    answer = "";
    await checkpoint();
    if (activityIndex >= lesson.activities.length) void finish();
    else void draw();
  };

  const draw = async () => {
    if (cancelled) return;
    if (activityIndex >= lesson.activities.length) return finish();
    submitting = true;
    const activity = lesson.activities[activityIndex];
    const question = activity.questions[questionIndex];
    // A reload resumes after committed input, never re-asks a question whose answer was revealed.
    if (responses.has(activity.id) || (question && answers.get(activity.id)?.[question.id] !== undefined)) {
      if (!responses.has(activity.id) && questionIndex + 1 < activity.questions.length) {
        questionIndex += 1;
        return draw();
      }
      return nextActivity();
    }
    await checkpoint();
    if (cancelled) return;
    submitting = false;
    const progress = ((activityIndex + 1) / lesson.activities.length) * 100;
    shell(`<div class="topbar"><button id="stop" class="icon-button" aria-label="Stop missie">×</button><span class="brand">Nieuwe rekentruc</span></div>
      <section class="lesson panel">
        <p class="eyebrow">Stap ${activityIndex + 1} van ${lesson.activities.length}</p>
        <div class="progress-line"><span style="width:${progress}%"></span></div>
        <h1>${escapeHtml(activity.title)}</h1>
        <div class="lesson-copy">${activity.copy.map((line) => `<p>${escapeHtml(line)}</p>`).join("")}</div>
        ${activity.expression ? `<div class="lesson-expression">${escapeHtml(activity.expression)}</div>` : ""}
        ${question ? `<p class="eyebrow">${escapeHtml(question.prompt)}</p><div id="answer" class="answer-display empty"></div>${keypadHtml()}${activity.phase === "INDEPENDENT_CHECK" ? `<button id="lesson-help" class="help">Ik weet 'm even niet</button>` : ""}<div id="lesson-feedback" class="feedback" role="status"></div>` : `<button id="next" class="primary wide">${activity.phase === "EXPLANATION" ? "Kijk mee" : "Verder"}</button>`}
      </section>`, "home", true);
    document.querySelector("#stop")?.addEventListener("click", async () => {
      if (submitting) return;
      cancelled = true;
      await runtime.stopSession(item.presentation.sessionId);
      activeSessionId = null;
      await renderHome();
    });
    if (!question) {
      document.querySelector("#next")?.addEventListener("click", nextActivity);
      return;
    }
    document.querySelector("#lesson-help")?.addEventListener("click", async () => {
      if (submitting || cancelled) return;
      submitting = true;
      responses.set(activity.id, {
        activityId: activity.id, answers: {}, helpRequested: true, supportUsed: true,
      });
      await checkpoint();
      const fact = FACT_CATALOG.factById.get(activity.factId!)!;
      shell(`<section class="lesson panel"><p class="eyebrow">Samen verder</p><h1>We pakken 'm samen</h1>
        <div class="lesson-copy">${strategyWorkedExample(strategyId, fact).map((line) => `<p>${escapeHtml(line)}</p>`).join("")}</div>
        <button id="continue-lesson" class="primary wide">Verder</button></section>`, "home", true);
      document.querySelector("#continue-lesson")?.addEventListener("click", nextActivity, { once: true });
    });
    const display = document.querySelector<HTMLDivElement>("#answer")!;
    const update = () => {
      display.textContent = answer;
      display.classList.toggle("empty", answer.length === 0);
    };
    document.querySelectorAll<HTMLButtonElement>("[data-digit]").forEach((button) => {
      button.addEventListener("click", () => {
        if (submitting || cancelled || answer.length >= 3) return;
        answer += button.dataset.digit;
        update();
      });
    });
    document.querySelector("[data-backspace]")?.addEventListener("click", () => {
      if (submitting || cancelled) return;
      answer = answer.slice(0, -1);
      update();
    });
    document.querySelector("[data-confirm]")?.addEventListener("click", async () => {
      if (submitting || cancelled || answer.length === 0) return;
      submitting = true;
      const value = Number(answer);
      const activityAnswers = answers.get(activity.id) ?? {};
      activityAnswers[question.id] = value;
      answers.set(activity.id, activityAnswers);
      await checkpoint();
      const correct = value === question.expectedAnswer;
      const feedback = document.querySelector<HTMLDivElement>("#lesson-feedback")!;
      feedback.className = `feedback ${correct ? "good" : "try"}`;
      feedback.textContent = correct ? "Ja, precies." : `Kijk: het is ${question.expectedAnswer}.`;
      document.querySelectorAll<HTMLButtonElement>(".key").forEach((button) => button.disabled = true);
      window.setTimeout(() => {
        if (questionIndex + 1 < activity.questions.length) {
          questionIndex += 1;
          answer = "";
          draw();
        } else {
          nextActivity();
        }
      }, correct ? 450 : 1050);
    });
  };
  draw();
}

async function renderSessionEnd(session: SessionRecord): Promise<void> {
  if (session.phase === "REVEAL_PENDING") {
    const unlocks = await repository.listAchievementUnlocks(PROFILE_ID);
    const sessionUnlocks = unlocks.filter((unlock) => session.achievementUnlockIds.includes(unlock.id));
    const cards = sessionUnlocks
      .map((unlock) => ACHIEVEMENT_CATALOG.cards.find((card) => card.id === unlock.cardId))
      .filter((card) => card !== undefined);
    shell(`<section style="text-align:center">
      <p class="eyebrow">Nieuwe kaart${cards.length === 1 ? "" : "en"}!</p>
      <h1>Dit heb jij verdiend</h1>
      <div class="card-grid reveal-grid" style="margin:28px 0">
        ${cards.map((card) => achievementCardMarkup(card, true)).join("")}
      </div>
      <button id="binder" class="primary wide">In mijn binder</button>
    </section>`, "cards", true);
    document.querySelector("#binder")?.addEventListener("click", async () => {
      const next = await runtime.finishReveals(session.id);
      await renderSessionSummary(next);
    });
    return;
  }
  await renderSessionSummary(session);
}

async function renderSessionSummary(session: SessionRecord): Promise<void> {
  const gap = session.completionReason === "CONTENT_GAP";
  const doneForNow = session.completionReason === "DONE_FOR_NOW";
  shell(`<section>
    <p class="eyebrow">${gap ? "Even pauze" : "Missie voltooid"}</p><h1>${gap ? "Hier gaan we later verder" : doneForNow ? "Genoeg geoefend voor nu" : "Klaar voor vandaag"}</h1>
    <div class="panel stack" style="margin:28px 0">
      <div class="metric"><span>Sommen en leerstappen</span><strong>${session.coreAttemptCount + session.bonusAttemptCount}</strong></div>
      <div class="metric"><span>Zelf hulp gekozen</span><strong>${session.helpCount}</strong></div>
      <p class="muted" style="margin:0">Alles wat je vandaag leerde, blijft bewaard.</p>
      ${gap ? `<p class="muted">Voor ouders: de proefversie mist nog een passende leeractiviteit. Dit is geen beoordeling van wat je kind kan.</p>`
        : doneForNow ? `<p class="muted">We geven deze sommen nu wat rust. Je hoeft ze niet steeds opnieuw te doen.</p>` : ""}
    </div>
    <div class="stack"><button id="done" class="primary wide">Klaar</button>${session.bonusRoundOffered && !session.bonusRoundAccepted && !session.completionReason ? `<button id="bonus" class="secondary wide">Bonusronde</button>` : ""}</div>
  </section>`, "home", true);
  document.querySelector("#done")?.addEventListener("click", async () => {
    await runtime.stopSession(session.id);
    activeSessionId = null;
    await renderHome();
  });
  document.querySelector("#bonus")?.addEventListener("click", async () => {
    await runtime.startBonus(session.id);
    await showNextItem();
  });
}

async function renderCards(): Promise<void> {
  const unlocks = await repository.listAchievementUnlocks(PROFILE_ID);
  const earned = new Map(unlocks.map((unlock) => [unlock.cardId, unlock]));
  shell(`<div class="row between"><div><span class="brand">Rekenheld</span><p class="eyebrow" style="margin-top:28px">Mijn kaarten</p></div><strong>${earned.size} / ${ACHIEVEMENT_CATALOG.cards.length}</strong></div>
    <h1>Mijn binder</h1>
    <p class="muted">Elke kaart is een echt bewijs van iets dat jij hebt geleerd.</p>
    <div class="card-grid" style="margin-top:24px">
      ${ACHIEVEMENT_CATALOG.cards.map((card) => {
        const unlock = earned.get(card.id);
        return achievementCardMarkup(card, Boolean(unlock));
      }).join("")}
    </div>`, "cards");
}

async function renderGrowth(): Promise<void> {
  const mastery = await repository.listSkillMastery(PROFILE_ID);
  const byId = new Map(mastery.map((item) => [item.skillId, item]));
  const visible = [
    SKILL_IDS.PLUS_MINUS_1,
    SKILL_IDS.PLUS_MINUS_2,
    SKILL_IDS.DOUBLES,
    SKILL_IDS.NEAR_DOUBLES,
    SKILL_IDS.MAKE_10,
    SKILL_IDS.BASIC_TO_10,
    SKILL_IDS.TEEN_NO_BRIDGE,
    SKILL_IDS.CROSS_10_ADD,
    SKILL_IDS.CROSS_10_SUB,
  ].map((skillId) => SKILLS.find((skill) => skill.id === skillId)!);
  shell(`<span class="brand">Rekenheld</span><p class="eyebrow" style="margin-top:28px">Mijn groei</p><h1>Kijk wat jij al kunt</h1>
    <section class="panel">
      ${visible.map((skill) => {
        const projection = byId.get(skill.id);
        return `<div class="skill-row"><strong>${escapeHtml(skill.label)}</strong><br><span class="status">${escapeHtml(projection?.childStatus ?? "Aan het leren")}</span></div>`;
      }).join("")}
    </section>`, "growth");
}

function strategyStatus(stage: string): string {
  return ({
    UNSEEN: "nog niet gestart",
    INTRODUCED: "uitleg gezien",
    GUIDED: "met begeleiding",
    INDEPENDENT: "aanpak zelfstandig",
    STABLE: "aanpak stabiel",
  } as Record<string, string>)[stage] ?? stage.toLowerCase();
}

async function renderParent(): Promise<void> {
  const [sessions, attempts, skills, strategies, unlocks] = await Promise.all([
    repository.listSessionsForProfile(PROFILE_ID),
    repository.listAttempts(PROFILE_ID),
    repository.listSkillMastery(PROFILE_ID),
    repository.listStrategyMastery(PROFILE_ID),
    repository.listAchievementUnlocks(PROFILE_ID),
  ]);
  const dates = new Set(weekDates());
  const thisWeek = sessions.filter((session) => dates.has(session.localDate));
  const minutes = Math.round(thisWeek.reduce((sum, session) => {
    if (!session.endedAt) return sum;
    return sum + Math.max(0, Date.parse(session.endedAt) - Date.parse(session.startedAt));
  }, 0) / 60_000);
  const masteredFacts = attempts.length === 0
    ? 0
    : (await repository.listFactMastery(PROFILE_ID)).filter((item) => item.highestStageEver === "MASTERED").length;
  const parentSkillOrder = [
    SKILL_IDS.PLUS_MINUS_1, SKILL_IDS.PLUS_MINUS_2,
    SKILL_IDS.DOUBLES, SKILL_IDS.NEAR_DOUBLES,
    SKILL_IDS.BASIC_TO_10, SKILL_IDS.MAKE_10,
    SKILL_IDS.TEEN_NO_BRIDGE, SKILL_IDS.CROSS_10_ADD, SKILL_IDS.CROSS_10_SUB,
  ];
  const skillById = new Map(skills.map((item) => [item.skillId, item]));
  const visibleSkills = parentSkillOrder.map((id) => skillById.get(id)).filter((item) => item !== undefined);
  const immediate = visibleSkills.filter((item) => item.currentStage === "KNOWS_IMMEDIATELY");
  const developing = visibleSkills.filter((item) => item.currentStage === "LEARNING" || item.currentStage === "STRENGTHENING");
  const developingNames = developing.map((item) => SKILLS.find((skill) => skill.id === item.skillId)?.label.toLowerCase()).filter(Boolean);
  const developingCopy = developingNames.length <= 3
    ? developingNames.join(", ").replace(/, ([^,]*)$/, " en $1")
    : `${developingNames.slice(0, 3).join(", ")} en nog ${developingNames.length - 3}`;
  const summary = immediate.length > 0
    ? `${immediate.map((item) => SKILLS.find((skill) => skill.id === item.skillId)?.label).filter(Boolean).join(", ")} is direct beschikbaar. Rekenheld onderhoudt dit met rustige spreiding.`
    : developing.length > 0
      ? `Anthony werkt momenteel aan ${developingCopy}. Strategie begrijpen en sommen direct beheersen zijn twee verschillende stappen.`
      : "De eerste sessies bouwen stil een betrouwbaar beeld op van wat Anthony al kan.";
  shell(`<div class="row between"><span class="brand">Voor ouders</span><button id="back" class="quiet">Terug</button></div>
    <p class="eyebrow" style="margin-top:32px">Rekenheld</p><h1>Anthony's ontwikkeling</h1>
    <section class="panel" style="margin-bottom:18px"><h2>In gewone taal</h2><p class="muted">${escapeHtml(summary)}</p></section>
    <section class="panel" style="margin-bottom:18px"><h2>Deze week</h2>
      <div class="metric"><span>Oefendagen</span><strong>${new Set(thisWeek.map((session) => session.localDate)).size}</strong></div>
      <div class="metric"><span>Afgeronde minuten</span><strong>${minutes}</strong></div>
      <div class="metric"><span>Direct beheerste sommen</span><strong>${masteredFacts}</strong></div>
      <div class="metric"><span>Verdiende kaarten</span><strong>${unlocks.length}</strong></div>
    </section>
    <section class="panel" style="margin-bottom:18px"><h2>Vaardigheden</h2>
      ${visibleSkills.length === 0 ? `<p class="muted">Nog onvoldoende oefenbewijs voor een vaardigheidsoverzicht.</p>` : visibleSkills.map((mastery) => `<div class="metric"><span>${escapeHtml(SKILLS.find((skill) => skill.id === mastery.skillId)?.label ?? mastery.skillId)}<br><small class="muted">${mastery.independentFactCount} van ${mastery.factCount} sommen zelfstandig</small></span><strong>${escapeHtml(mastery.childStatus)}</strong></div>`).join("")}
    </section>
    <section class="panel"><h2>Rekenaanpakken</h2>
      ${strategies.length === 0 ? `<p class="muted">Nog onvoldoende bewijs voor een strategieoverzicht.</p>` : strategies.map((mastery) => `<div class="metric"><span>${escapeHtml(STRATEGY_BY_ID.get(mastery.strategyId)?.label ?? mastery.strategyId)}</span><strong>${escapeHtml(strategyStatus(mastery.currentStage))}</strong></div>`).join("")}
      <p class="muted" style="margin:16px 0 0">Een stabiele aanpak betekent dat Anthony de rekentruc kan gebruiken. De vaardigheid groeit daarna verder doordat verschillende sommen zelfstandig lukken.</p>
    </section>`, "home", true);
  document.querySelector("#back")?.addEventListener("click", () => void renderHome());
}

async function initialize(): Promise<void> {
  const indexed = await IndexedDbLearningRepository.open();
  repository = indexed;
  const existing = await repository.getProfile(PROFILE_ID);
  if (!existing) {
    await repository.putProfile(defaultLearnerProfile(
      PROFILE_ID,
      "Anthony",
      new Date().toISOString(),
    ));
  }
  runtime = new RekenheldRuntime({ repository });
  const resumable = await latestResumableSession();
  activeSessionId = resumable?.id ?? null;
  await renderHome();
  if ("serviceWorker" in navigator) {
    void navigator.serviceWorker.register("./service-worker.js");
  }
}

void initialize().catch((error: unknown) => {
  const message = error instanceof Error ? error.message : "Onbekende fout";
  app.innerHTML = `<main class="shell"><section class="error-box"><p class="eyebrow">Rekenheld kon niet starten</p><h1>Even opnieuw</h1><p>${escapeHtml(message)}</p><button class="primary" onclick="location.reload()">Probeer opnieuw</button></section></main>`;
});

window.addEventListener("unhandledrejection", (event) => {
  event.preventDefault();
  const message = event.reason instanceof Error ? event.reason.message : "De leeractiviteit kon niet worden opgeslagen.";
  shell(`<section class="error-box"><h1>Even opnieuw openen</h1><p>Je opgeslagen voortgang blijft bewaard.</p><p>${escapeHtml(message)}</p><button id="reload" class="primary wide">Opnieuw openen</button></section>`, "home", true);
  document.querySelector("#reload")?.addEventListener("click", () => location.reload());
});
